import { useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChatContextType,
  ChatCustomFilter,
  ChatFilterOperator,
  type AgentFilter,
  type CustomFilter,
} from '@trinity/chat';
import {
  ActiveChatSessionContext,
  AuthorContext,
  ChatSessionFiltersContext,
} from '@trinity/chat-components/components/Chat/context';
import { FilterTypeEnum } from '@trinity/chat-components/components/Chat.Filters/interfaces';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { AgentResourcesContext, type AgentResourcesContextValue } from './context';

type Props = {
  children: ReactNode;
};

type TerritoryResourcesContainerProps = {
  agentIdentifier: string | null;
  enabled: boolean;
  isAgentContext: boolean;
  filters: AgentFilter[];
  children: (data: {
    territoryOptions: CustomFilter.ItemValue[];
    hasLoadedTerritories: boolean;
  }) => ReactNode;
};

function TerritoryResourcesContainer({
  agentIdentifier,
  enabled,
  isAgentContext,
  filters,
  children,
}: TerritoryResourcesContainerProps) {
  const apiClient = useChatApiClient();
  const author = useContext(AuthorContext);
  const sessionFilters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);

  const territoryFilter = useMemo(() => {
    return filters.find((filter) => filter.type === FilterTypeEnum.UserTerritory);
  }, [filters]);

  const hasUserTerritoryFilter = territoryFilter !== undefined;

  const userTerritoriesQuery = useQuery({
    queryKey: ['user-territories', agentIdentifier, apiClient.agentResources],
    queryFn: () => apiClient.agentResources.getUserTerritories(),
    enabled: enabled && hasUserTerritoryFilter,
    initialData: { territories: [] },
  });

  const territoryOptions = useMemo(() => {
    return userTerritoriesQuery.data.territories.map((territory) => ({
      id: territory.territoryKey,
      name: territory.name,
    }));
  }, [userTerritoriesQuery.data.territories]);

  const hasLoadedTerritories = useMemo(() => {
    if (!hasUserTerritoryFilter) {
      return false;
    }

    return !userTerritoriesQuery.isLoading;
  }, [hasUserTerritoryFilter, userTerritoriesQuery.isLoading]);

  useEffect(() => {
    if (!isAgentContext || !territoryFilter) {
      return;
    }

    if (!hasLoadedTerritories) {
      return;
    }

    const defaultTerritoryId = author?.metadata?.defaultTerritoryId;
    if (typeof defaultTerritoryId !== 'string' || defaultTerritoryId.length === 0) {
      return;
    }

    const defaultTerritoryOption = territoryOptions.find((option) => option.id === defaultTerritoryId);
    if (!defaultTerritoryOption) {
      return;
    }

    const hasTerritoryFilterSet = (sessionFilters.custom || []).some((customFilter) => {
      if (customFilter.type !== ChatCustomFilter.AgentFilter) {
        return false;
      }

      const firstValue = customFilter.value[0] as (CustomFilter.ItemValue & { filterId?: number | string }) | undefined;
      if (!firstValue?.filterId) {
        return false;
      }

      return Number(firstValue.filterId) === territoryFilter.id;
    });

    if (hasTerritoryFilterSet) {
      return;
    }

    const territoryValue = {
      ...defaultTerritoryOption,
      filterId: territoryFilter.id,
      filterLabel: territoryFilter.label,
    } as CustomFilter.ItemValue;

    dispatch({
      type: 'filters/add-custom-filter',
      payload: {
        value: {
          type: ChatCustomFilter.AgentFilter,
          operator: [ChatFilterOperator.Eq],
          value: [territoryValue],
        },
      },
    });
  }, [
    author?.metadata?.defaultTerritoryId,
    dispatch,
    hasLoadedTerritories,
    isAgentContext,
    sessionFilters.custom,
    territoryFilter,
    territoryOptions,
  ]);

  return <>{children({ territoryOptions, hasLoadedTerritories })}</>;
}

export function AgentResourcesProvider({ children }: Props) {
  const session = useContext(ActiveChatSessionContext);
  const apiClient = useChatApiClient();

  const agentIdentifier = useMemo(() => {
    return session.context.type === ChatContextType.Agent
      ? session.context.data.agent.identifier
      : null;
  }, [session.context.data, session.context.type]);

  const agentResourcesEnabled = useMemo(() => {
    return agentIdentifier !== null;
  }, [agentIdentifier]);

  const agentResourcesQuery = useQuery({
    queryKey: ['agent-resources', agentIdentifier, apiClient.agentResources],
    queryFn: () => {
      if (!agentIdentifier) {
        return Promise.resolve({ dataSources: [], filters: [] });
      }
      return apiClient.agentResources.getAgentResources({ agentIdentifier });
    },
    enabled: agentResourcesEnabled,
    initialData: { dataSources: [], filters: [] },
  });

  return (
    <TerritoryResourcesContainer
      agentIdentifier={agentIdentifier}
      enabled={agentResourcesEnabled}
      isAgentContext={session.context.type === ChatContextType.Agent}
      filters={agentResourcesQuery.data.filters}
    >
      {({ territoryOptions, hasLoadedTerritories }) => {
        const contextValue: AgentResourcesContextValue = {
          data: agentResourcesQuery.data,
          isLoading: agentResourcesQuery.isLoading,
          isError: agentResourcesQuery.isError,
          error: agentResourcesQuery.error,
          territoryOptions,
          hasLoadedTerritories,
        };

        return (
          <AgentResourcesContext.Provider value={contextValue}>
            {children}
          </AgentResourcesContext.Provider>
        );
      }}
    </TerritoryResourcesContainer>
  );
}

export function useAgentResources(): AgentResourcesContextValue {
  const context = useContext(AgentResourcesContext);

  if (!context) {
    throw new Error('useAgentResources must be used within an AgentResourcesProvider');
  }

  return context;
}